import { apiPaths, RefreshIntervalsEnum } from 'configs/apiConfigs'
import { getCallsByClaimId } from 'features/calls/callsApi'
import { CallStatusEnum, ICall } from 'features/calls/types'
import useSWR, { KeyedMutator, SWRConfiguration } from 'swr'
import { OperatorTypeEnum } from 'types/tableTypes'

type PublicConfiguration = SWRConfiguration
import { dotNetInteger32 } from 'utils'
import { encodeUri } from 'utils/api'

import { ICsvPreviewRequest, ICsvPreviewSniffRequest, IKvpMapGetKey } from '../settings/settingsTypes'
import {
  getClaimDetails,
  getClaimEntityVersion,
  getClaims,
  getCsvPreview,
  getCsvPreviewSniff,
  getDdfColumSettingsAvailable,
  getKvpMapKey,
  getLevelofSupport,
  getClaimStatusLookup,
  getDetailedClaimStatusLookup,
} from './claimsApi'
import { ClaimStatusTypeEnum, IClaim } from './types'

export const useClaims = (apiQuery?: string | null, config?: SWRConfiguration) => {
  const apiQueryWithDefaultCreatedDescSorting = apiQuery?.includes('Sort=')
    ? apiQuery
    : `${apiQuery}&Sort=${encodeUri({
        path: 'created',
        descending: true,
      })}`

  const { data, error, mutate } = useSWR(
    apiQuery ? apiPaths.claims(apiQueryWithDefaultCreatedDescSorting) : null,
    getClaims,
    {
      refreshInterval: RefreshIntervalsEnum.useClaims,
      ...(config ? config : {}),
    },
  )

  return {
    data,
    error,
    isLoading: !data && !error,
    mutate,
  }
}

export const useClaim = (oaiClaimId?: string | null, config?: SWRConfiguration) => {
  const { data, error, mutate } = useSWR(oaiClaimId ? apiPaths.claim(oaiClaimId) : null, getClaimDetails, {
    ...(config ? config : {}),
  })
  return {
    data,
    error,
    isLoading: !data && !error,
    mutate,
  }
}

export const useDiagnosticClaim = (config?: Partial<PublicConfiguration>) => {
  const { data, error, mutate } = useSWR(apiPaths.diagnosticClaim, getClaimDetails, {
    ...(config ? config : {}),
  })
  return {
    data: data as IClaim | undefined,
    error,
    isLoading: !data && !error,
    mutate,
  }
}

export const useClaimCalls = (claimId?: string | null, config?: any) => {
  const filterCalls = [
    CallStatusEnum.Complete,
    CallStatusEnum.InProgress,
    CallStatusEnum.FailedToComplete,
    CallStatusEnum.RetryPending,
    CallStatusEnum.Queued,
    CallStatusEnum.FailedToQueue,
    CallStatusEnum.InProgress,
  ]
  const query = []
  query.push(`?Limit=${dotNetInteger32}`)
  query.push(
    `Filter=${encodeUri({
      operator: OperatorTypeEnum.in,
      path: 'status',
      valueList: filterCalls,
    })}`,
  )

  const { data, error, mutate, isValidating } = useSWR(
    claimId ? apiPaths.claimCalls(claimId, query.join('&')) : null,
    getCallsByClaimId,
    { ...(config ? config : {}) },
  )

  const addCallToClaimCallsCache = (newCall: ICall) =>
    mutate((calls) => (calls ? [...calls, newCall] : [newCall]), false)

  return {
    data,
    error,
    isLoading: !data && !error,
    addCallToClaimCallsCache,
    isValidating,
    mutate,
  }
}

export const useCurrentUserClaims = (id?: string, type?: 'pendingDisposition' | 'dispositioned', config?: any) => {
  const query = id
    ? `?Filter=${encodeUri({
        path: 'ownerId',
        operator: '=',
        value: id,
      })}&Filter=${encodeUri({
        path: 'oaiStatus',
        operator: OperatorTypeEnum.in,
        valueList:
          type === 'dispositioned'
            ? [ClaimStatusTypeEnum.workCompleted]
            : [ClaimStatusTypeEnum.unknown, ClaimStatusTypeEnum.inProgress, ClaimStatusTypeEnum.statusUpdateNeeded],
      })}&Sort=${encodeUri({
        path: 'created',
        descending: true,
      })}&Limit=1000`
    : ''

  const { data, error, mutate } = useSWR(id ? apiPaths.claims(query) : null, getClaims, {
    refreshInterval: RefreshIntervalsEnum.useCurrentUserClaims,
    ...(config ? config : {}),
  })

  return {
    data,
    error,
    isLoading: !data && !error,
    mutate,
  }
}

export const useClaimEntityVersion = (url: string | null) => {
  const { data, error, mutate } = useSWR(url, getClaimEntityVersion)

  return { data, error, isLoading: !data && !error, mutate }
}

export const useLevelofSupport = () => {
  const { data, error } = useSWR(apiPaths.levelOfSupports(), getLevelofSupport)
  return { data, error, isLoading: !data && !error }
}

export const useKvpMapKey = (
  key?: string,
): { data: IKvpMapGetKey | undefined; error: any; isLoading: boolean; mutate: KeyedMutator<IKvpMapGetKey> } => {
  const { data, error, mutate } = useSWR(apiPaths.kvpGetMapKey(key), getKvpMapKey)
  return { data, error, isLoading: !data && !error, mutate }
}

export const useCsvPreviewSniff = (csvPreviewSniffRequest: ICsvPreviewSniffRequest) => {
  const { data, error, mutate } = useSWR({ path: apiPaths.csvPreview(), data: csvPreviewSniffRequest }, () =>
    getCsvPreviewSniff(csvPreviewSniffRequest),
  )
  return { data, error, isLoading: !data && !error, mutate }
}

export const useCsvPreview = (csvPreviewRequest: ICsvPreviewRequest) => {
  const { data, error, mutate } = useSWR({ path: apiPaths.csvPreview(), data: csvPreviewRequest }, () =>
    getCsvPreview(csvPreviewRequest),
  )
  return { data, error, isLoading: !data && !error, mutate }
}

export const useDdfColumSettingsAvailable = () => {
  const { data, error } = useSWR(apiPaths.ddfColumSettingsAvailable(), getDdfColumSettingsAvailable)
  return { data, error, isLoading: !data && !error }
}

export const useClaimStatuses = () => {
  const { data: options, error } = useSWR(apiPaths.claimStatuses(), getClaimStatusLookup)
  return {
    data: options?.map((option) => option.name),
    options: options ?? [],
    error,
    isLoading: !options && !error,
  }
}

export const useDetailedClaimStatuses = () => {
  const { data: options, error } = useSWR(apiPaths.detailedClaimStatuses(), getDetailedClaimStatusLookup)
  return {
    data: options?.map((option) => option.name),
    options: options ?? [],
    error,
    isLoading: !options && !error,
  }
}
